/* bios-pointcloud.js — Three.js point-cloud background for the portal.
   ──────────────────────────────────────────────────────────────────────────
   Renders a slowly-rotating cloud of white dots in the portal background.
   The cloud's vertex positions come from a .ply file you scan with Polycam
   (or any photogrammetry tool) and drop into /assets. Until you upload one,
   a procedural sphere of points renders as a placeholder so the pipeline
   is visibly working.

   Loading order:
     1. This script is loaded synchronously from sections/main-content.liquid
        inside the portal block.
     2. It lazily injects Three.js from jsDelivr (SRI-pinned).
     3. Once Three loads, it tries to fetch the .ply file from the URL passed
        in window.__INNOCENCES_POINT_CLOUD_URL__ (also set inline in main-
        content.liquid via {{ ... | asset_url }}).
     4. If the fetch fails or the file is missing, falls back to the
        procedural sphere — you'll see something either way.

   To swap products:
     Upload a new .ply file via Shopify admin → Online Store → Themes →
     Edit Code → Assets → Add a new asset. Name it `innocences-scan.ply`
     and the page picks it up on next reload. (Or change the filename in
     the asset_url line of main-content.liquid.)

   Supports both ASCII and binary_little_endian PLY (Polycam exports
   binary by default). Strips vertex colors and renders pure white dots
   to match the BIOS aesthetic — the rest of the theme is monochrome
   amber/white, the cloud follows. */
(function biosPointCloud() {
  'use strict';

  var portal = document.getElementById('bios-portal');
  if (!portal) { console.info('[pointcloud] no #bios-portal in DOM — skipping'); return; }

  /* Respect prefers-reduced-motion but don't bail entirely. On iOS Low
     Power Mode this flag is on by default — a huge fraction of mobile
     visitors. Instead of skipping the cloud, we render the static
     silhouette and skip the rotation + breathing-pulse RAF loop. */
  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Three.js — try jsdelivr first, fall back to unpkg if blocked (some
     CSPs or networks block one but not the other). Version-pinned to
     0.160.0 for reproducibility. To self-host: drop three.min.js into
     /assets and add it as the first entry. */
  var THREE_URLS = [
    'https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.min.js',
    'https://unpkg.com/three@0.160.0/build/three.min.js'
  ];

  function loadThree(urls, idx, cb) {
    if (window.THREE) { cb(); return; }
    if (idx >= urls.length) {
      console.error('[pointcloud] Three.js failed to load from all CDNs — point cloud disabled');
      return;
    }
    var s = document.createElement('script');
    s.src = urls[idx];
    s.crossOrigin = 'anonymous';
    s.onload  = function () { console.info('[pointcloud] Three.js loaded from', urls[idx]); cb(); };
    s.onerror = function () {
      console.warn('[pointcloud] Three.js CDN failed:', urls[idx], '— trying next');
      loadThree(urls, idx + 1, cb);
    };
    document.head.appendChild(s);
  }

  loadThree(THREE_URLS, 0, init);

  function init() {
    if (!window.THREE) return;
    var THREE = window.THREE;

    var canvas = document.getElementById('bios-portal-pointcloud');
    if (!canvas) return;

    /* ── Scene setup ── */
    var scene  = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(48, window.innerWidth / window.innerHeight, 0.1, 50);
    camera.position.z = 4.5;

    var renderer = new THREE.WebGLRenderer({
      canvas: canvas,
      alpha: true,            /* see through to the existing portal bg */
      antialias: false,       /* dot rendering doesn't need it; saves perf */
      powerPreference: 'low-power'
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    sizeRenderer();

    var group = new THREE.Group();
    scene.add(group);

    /* ── Load scan file or fall back to placeholder.
         Liquid passes an ordered list of candidate { type, url, bin? }
         entries — each format × each possible Shopify location (Files
         section AND theme Assets folder). We walk through them in order,
         skipping entries whose URL is empty (Liquid's file_url returns
         empty for missing files) or that 404 on fetch. First one that
         loads + parses successfully wins. If none do, the procedural
         placeholder fills in. ── */
    var candidates = window.__INNOCENCES_POINT_CLOUD_CANDIDATES__ || [];

    /* Back-compat: older shapes (map, single URL) get converted. */
    if (!candidates.length) {
      var files = window.__INNOCENCES_POINT_CLOUD_FILES__ || {};
      if (window.__INNOCENCES_POINT_CLOUD_URL__ && !files.ply && !files.glb && !files.gltf) {
        var ext = (window.__INNOCENCES_POINT_CLOUD_URL__.split('?')[0].split('.').pop() || '').toLowerCase();
        files[ext] = window.__INNOCENCES_POINT_CLOUD_URL__;
      }
      if (files.glb)  candidates.push({ type: 'glb',  url: files.glb });
      if (files.gltf) candidates.push({ type: 'gltf', url: files.gltf, bin: files.bin });
      if (files.ply)  candidates.push({ type: 'ply',  url: files.ply });
    }

    /* ── Layered 3-scan portal background ─────────────────────────────
       Load three uploaded GLB scans in parallel and render them as three
       cross-fading Points layers. Each scan peaks in opacity at its own
       phase of a slow cycle (120° apart), so the visible texture is the
       weighted superposition of all three — a unique composite that
       morphs from one form to the next.

       If any scan fails (404, network, parse), we fall back to the
       procedural body-blob morph so the portal still has a cloud
       behind the wordmark. */
    var LAYERED_SCAN_URLS = [
      'https://cdn.shopify.com/3d/models/8aad3dcd24bc8431/2026-05-20.glb',
      'https://cdn.shopify.com/3d/models/5cab79a5a02d529d/2026-05-20_2.glb',
      'https://cdn.shopify.com/3d/models/e90086e41a317873/2026-05-20_3.glb'
    ];

    Promise.all(LAYERED_SCAN_URLS.map(function (url) {
      return timedFetch(url, 12000)
        .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.arrayBuffer(); })
        .then(function (buf) { return parseGLB(buf); });
    })).then(function (scans) {
      /* Normalize each scan independently so they all fit the same
         viewport area regardless of source-file scale/translation. */
      scans = scans.map(function (s) { return normalize(s.positions); });
      addLayeredScanClouds(scans);
    }).catch(function (err) {
      console.warn('[pointcloud] layered scan load failed — falling back to procedural', err && err.message);
      addCloud(placeholderSphere());
    });

    /* Fetch with a soft timeout so a hung cross-origin URL doesn't lock
       the candidate fall-through forever. AbortController is broadly
       supported (all modern browsers); if it's missing, the fetch just
       waits naturally. */
    function timedFetch(url, ms) {
      var ac = (typeof AbortController !== 'undefined') ? new AbortController() : null;
      var to = setTimeout(function () { if (ac) ac.abort(); }, ms);
      var opts = { credentials: 'omit' };
      if (ac) opts.signal = ac.signal;
      return fetch(url, opts).finally(function () { clearTimeout(to); });
    }

    function tryNext(i) {
      if (i >= candidates.length) {
        console.info('[pointcloud] no scan resolved — using procedural placeholder');
        addCloud(placeholderSphere()); return;
      }
      var c = candidates[i];
      /* Skip empty URLs — Liquid renders '' for missing files. */
      if (!c.url) { tryNext(i + 1); return; }
      if (c.type === 'gltf') {
        if (!c.bin) { tryNext(i + 1); return; }
        loadGLTFPair(c.url, c.bin)
          .then(function (data) {
            console.info('[pointcloud] loaded gltf+bin:', c.url, '·', data.positions.length / 3, 'vertices');
            addCloud(data);
          })
          .catch(function (err) {
            console.info('[pointcloud] gltf failed:', c.url, err && err.message);
            tryNext(i + 1);
          });
        return;
      }
      timedFetch(c.url, 10000)
        .then(function (r) {
          if (!r.ok) throw new Error('http ' + r.status);
          return r.arrayBuffer();
        })
        .then(function (buf) {
          var data = (c.type === 'glb') ? parseGLB(buf) : parsePLY(buf);
          console.info('[pointcloud] loaded ' + c.type + ':', c.url, '·', data.positions.length / 3, 'vertices');
          addCloud(data);
        })
        .catch(function (err) {
          console.info('[pointcloud] ' + c.type + ' failed:', c.url, err && err.message);
          tryNext(i + 1);
        });
    }

    /* ── Mouse parallax ── */
    var mx = 0, my = 0, tx = 0, ty = 0;
    window.addEventListener('mousemove', function (e) {
      tx = (e.clientX / window.innerWidth)  * 2 - 1;
      ty = (e.clientY / window.innerHeight) * 2 - 1;
    }, { passive: true });

    /* ── RAF loop ── */
    var autoY  = 0;
    var stopped = false;
    var raf;
    /* Breathing pulse — slow sine modulates point size + opacity in unison
       so the cloud reads as alive rather than static geometry. Period ~6s
       (deep, meditative breath — slower than resting). Amplitudes kept
       subtle (±13% size, ±0.07 opacity) so it registers as ambient
       rather than animation. */
    var PULSE_PERIOD_MS  = 10000;   /* slower breath: 10s period */
    var PULSE_SIZE_AMP   = 0.06;    /* gentler: ±6% size */
    var PULSE_OPACITY_AMP = 0.03;   /* gentler: ±0.03 opacity */
    var t0 = performance.now();
    function tick() {
      if (stopped) return;
      raf = requestAnimationFrame(tick);

      /* Morphing-form view — slight 3/4 + small down-tilt so the
         body-blob silhouette reads as a figure standing in front of
         camera. Matches the Eastern Market vantage where you see the
         scanned figure from roughly eye level. Mouse parallax adds
         subtle camera drift; autonomous 360° turntable spin is added
         below by the layered-scan block, which reads the parallax
         offset from group.userData.parallaxY and composes the two. */
      mx += (tx - mx) * 0.06;
      my += (ty - my) * 0.06;
      group.rotation.x = -Math.PI * 0.04 + my * 0.10;
      /* Stash parallax Y offset; the spin block below combines it
         with the autonomous rotation so neither overwrites the other. */
      group.userData.parallaxY = mx * 0.22;
      group.rotation.z = 0;

      /* ── Per-point FLOW displacement ─────────────────────────────────
         Each vertex carries a unit direction vector + its own frequency
         and phase. We push it along that direction by sin(time*freq+ph)
         * amp; large amps (≈0.25) make the form deform substantially
         rather than just shimmer. Different freq/phase per point means
         different regions of the sphere drift out of sync — the form
         reads as continuously morphing rather than pulsing.

         A periodic glitch boost briefly doubles the amplitude for ~120ms,
         then snaps back — gives the form the occasional "scan corrupt"
         feel from the Eastern Market reference. */
      var elapsedSec = (performance.now() - t0) * 0.001;
      var TWO_PI = Math.PI * 2;
      var FLOW_AMP_BASE = 0.30;
      /* Glitch trigger: every 4–9 seconds, set a short window of
         increased displacement and lower opacity. */
      if (!window.__pcGlitch) window.__pcGlitch = { nextAt: 4, until: 0 };
      var gl = window.__pcGlitch;
      if (elapsedSec >= gl.nextAt && elapsedSec >= gl.until) {
        gl.until  = elapsedSec + 0.10 + Math.random() * 0.18;
        gl.nextAt = gl.until + 4 + Math.random() * 5;
      }
      var inGlitch  = elapsedSec < gl.until;
      var flowAmp   = inGlitch ? FLOW_AMP_BASE * 2.1 : FLOW_AMP_BASE;
      var glitchOpacity = inGlitch ? 0.55 : 1.0;

      /* Shape-morph weights — 4 cosines at 90° offsets, normalized.
         Period ~22s for a slow flow through the variants. The pow(_,2)
         emphasizes one dominant shape at each moment so the blend
         transitions through clear forms rather than averaging mush. */
      var MORPH_PERIOD = 22.0;
      var morphT = (elapsedSec / MORPH_PERIOD) * TWO_PI;
      var rw0 = 0.5 + 0.5 * Math.cos(morphT);
      var rw1 = 0.5 + 0.5 * Math.cos(morphT + Math.PI * 0.5);
      var rw2 = 0.5 + 0.5 * Math.cos(morphT + Math.PI);
      var rw3 = 0.5 + 0.5 * Math.cos(morphT + Math.PI * 1.5);
      rw0 *= rw0; rw1 *= rw1; rw2 *= rw2; rw3 *= rw3;
      var rwSum = rw0 + rw1 + rw2 + rw3 + 1e-9;
      var w0 = rw0 / rwSum, w1 = rw1 / rwSum, w2 = rw2 / rwSum, w3 = rw3 / rwSum;

      for (var bci = 0; bci < group.children.length; bci++) {
        var bchild = group.children[bci];
        if (!bchild.userData.isBreatherSource) continue;
        var bg = bchild.geometry;
        if (!bg.userData || !bg.userData.basePositions) continue;
        var basePos = bg.userData.basePositions;
        var breath  = bg.userData.breath;
        var work    = bg.userData.workArr;
        var shapes  = bg.userData.shapes;   /* may be null (alt scenes) */
        var nv      = basePos.length / 3;
        if (shapes && shapes.length === 4) {
          /* Multi-shape blend path: morphed base = w0·shape0 + … + w3·shape3,
             then add per-point flow displacement on top. */
          var s0 = shapes[0], s1 = shapes[1], s2 = shapes[2], s3 = shapes[3];
          for (var vi = 0; vi < nv; vi++) {
            var bo = vi * 5;
            var dx = breath[bo];
            var dy = breath[bo + 1];
            var dz = breath[bo + 2];
            var pf = breath[bo + 3];
            var pp = breath[bo + 4];
            var s  = Math.sin(elapsedSec * pf * TWO_PI + pp) * flowAmp;
            var i3 = vi * 3;
            var bx = w0 * s0[i3]     + w1 * s1[i3]     + w2 * s2[i3]     + w3 * s3[i3];
            var by = w0 * s0[i3 + 1] + w1 * s1[i3 + 1] + w2 * s2[i3 + 1] + w3 * s3[i3 + 1];
            var bz = w0 * s0[i3 + 2] + w1 * s1[i3 + 2] + w2 * s2[i3 + 2] + w3 * s3[i3 + 2];
            work[i3]     = bx + dx * s;
            work[i3 + 1] = by + dy * s;
            work[i3 + 2] = bz + dz * s;
          }
        } else {
          /* Fallback: static base + flow (used by alt scenes
             procWiredScene/procNetworkNodes when they're swapped in). */
          for (var vi2 = 0; vi2 < nv; vi2++) {
            var bo2 = vi2 * 5;
            var dx2 = breath[bo2];
            var dy2 = breath[bo2 + 1];
            var dz2 = breath[bo2 + 2];
            var pf2 = breath[bo2 + 3];
            var pp2 = breath[bo2 + 4];
            var s2_ = Math.sin(elapsedSec * pf2 * TWO_PI + pp2) * flowAmp;
            var i32 = vi2 * 3;
            work[i32]     = basePos[i32]     + dx2 * s2_;
            work[i32 + 1] = basePos[i32 + 1] + dy2 * s2_;
            work[i32 + 2] = basePos[i32 + 2] + dz2 * s2_;
          }
        }
        bg.attributes.position.needsUpdate = true;
      }

      /* Global breathing pulse + layered-scan crossfade.
         For regular clouds: subtle size/opacity pulse via PULSE_*.
         For layered-scan clouds (.isLayeredScan): opacity is driven by
         a cosine cycle offset by 120° per layer so each scan peaks in
         turn — the visible texture is the weighted superposition of all
         three, slowly rotating through dominance over a ~14s cycle.
         Glitch windows additionally dim everything. */
      var pulse = Math.sin((performance.now() - t0) * (Math.PI * 2 / PULSE_PERIOD_MS));
      var LAYER_CYCLE_PERIOD = 14000;
      var layerT = ((performance.now() - t0) / LAYER_CYCLE_PERIOD) * (Math.PI * 2);

      /* ── Whole-scene slow 360° pan ────────────────────────────────
         Rotate the entire scan group around Y so all layers sweep
         together — gives a "specimen on a turntable" feel without
         disturbing the per-layer drift/flicker that sits on top.
         Period = 120s/rev → ~3°/sec. Slow enough to read as ambient,
         not animation. Mouse parallax in onMouse() adds rotation on
         top of this base value (additive via .y assignment below
         compounded with the parallax write); we read the parallax
         offset out of group.userData.parallaxY (set by onMouse) and
         compose, so neither effect erases the other. */
      var SCAN_SPIN_PERIOD = 120000; /* ms per full revolution */
      var spinY = ((performance.now() - t0) / SCAN_SPIN_PERIOD) * (Math.PI * 2);
      var parallaxY = (group.userData && group.userData.parallaxY) || 0;
      group.rotation.y = spinY + parallaxY;

      for (var ci = 0; ci < group.children.length; ci++) {
        var child = group.children[ci];
        if (!child.material || child.userData.baseSize == null) continue;
        child.material.size = child.userData.baseSize * (1 + pulse * PULSE_SIZE_AMP);
        if (child.userData.isLayeredScan) {
          /* Layer opacity: cos(layerT + phase) maps to [-1,1]; remap to
             [0.18, 1.0] of baseOpacity so the trough layer never fully
             disappears — keeps the composite texture continuous. */
          var w = 0.5 + 0.5 * Math.cos(layerT + child.userData.layerPhase);
          var layerOp = (0.18 + 0.82 * w) * child.userData.baseOpacity;

          /* Per-layer drift — gentle sin/cos position + rotation offsets
             driven by per-layer phases. Layered on top of the group-
             level mouse parallax so the scans feel like they're each
             floating in their own current. */
          var driftT = elapsedSec * child.userData.driftFreq * Math.PI * 2;
          child.position.x = Math.sin(driftT + child.userData.driftPhaseX) * child.userData.driftAmpX;
          child.position.y = Math.cos(driftT + child.userData.driftPhaseY) * child.userData.driftAmpY;
          child.rotation.y = Math.sin(driftT + child.userData.driftPhaseR) * child.userData.driftAmpRot;

          /* Per-layer flicker — schedule the next short opacity blip
             when the current window expires. Blip duration 40–180ms,
             gap 2–9s between blips. While in a blip, opacity drops to
             ~25% so the layer reads as briefly glitching off-feed. */
          if (elapsedSec > child.userData.flickerNextAt && elapsedSec > child.userData.flickerUntil) {
            child.userData.flickerUntil = elapsedSec + 0.04 + Math.random() * 0.14;
            child.userData.flickerNextAt = child.userData.flickerUntil + 2 + Math.random() * 7;
          }
          var inFlicker = elapsedSec < child.userData.flickerUntil;
          var layerFlickerOp = inFlicker ? 0.25 : 1.0;

          child.material.opacity = layerOp * glitchOpacity * layerFlickerOp;
        } else {
          child.material.opacity = (child.userData.baseOpacity + pulse * PULSE_OPACITY_AMP) * glitchOpacity;
        }
      }

      renderer.render(scene, camera);
    }
    /* Always tick. The rotation is ~9°/sec (37s per full revolution) and
       the breathing pulse is ±13% size over 6s — both gentle enough to
       qualify as ambient rather than animation, even under
       prefers-reduced-motion. Mouse parallax + idle drift make the
       portal feel alive; killing all of that produced a flat still
       that looked like the scan had failed to load. */
    tick();

    /* ── Cleanup when the portal is dismissed (catalog opened or [STORE]
         navigation). Frees the WebGL context + RAF so we don't pay GPU/CPU
         cost while the user is inside the catalog. ── */
    document.addEventListener('bios:portal-done', function () {
      stopped = true;
      if (raf) cancelAnimationFrame(raf);
      try {
        scene.traverse(function (o) {
          if (o.geometry) o.geometry.dispose();
          if (o.material) o.material.dispose();
        });
        renderer.dispose();
      } catch (_) {}
    });

    /* ── Resize ── */
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(sizeRenderer, 80);
    });

    function sizeRenderer() {
      var w = window.innerWidth, h = window.innerHeight;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }

    /* ── Layered scan rendering ────────────────────────────────────────
       Renders an array of normalized point-cloud datas as N separate
       THREE.Points layers, each with its own material so the tick() loop
       can crossfade them via opacity. Color is the warm BIOS grey
       (matches the original portal nodes); each point is a tiny 1.6px
       dot, no glow, so density carries the form rather than blur.

       Each layer gets a phase offset in userData so opacity cycles 120°
       apart — at any moment one layer is dominant, the next is rising,
       the third is fading. */
    function addLayeredScanClouds(scans) {
      var N = scans.length;
      scans.forEach(function (data, idx) {
        var geom = new THREE.BufferGeometry();
        geom.setAttribute('position', new THREE.BufferAttribute(data.positions, 3));
        geom.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 6);
        var baseSize    = 1.6;
        /* Lowered from 0.62 → 0.48: the layered scans were reading a
           touch too solid against the portal text-shadow halo. Slight
           drop pushes them further into the "ambient backdrop" register
           so the wordmark + nav cluster stay the visual focus. The
           per-frame opacity curve in tick() (0.18 + 0.82 * w) still
           multiplies on top, so dominant-layer peak is now ~0.48 and
           trough is ~0.086. */
        /* bumped 2026-05-20: cache-invalidator for CDN hash refresh */
        var baseOpacity = 0.48;
        var mat = new THREE.PointsMaterial({
          color: 0xbeb9aa,                 /* warm grey — original portal node tone */
          size: baseSize,
          sizeAttenuation: false,
          transparent: true,
          opacity: 0.0,                    /* tick() drives the actual opacity each frame */
          depthWrite: false,
          depthTest: false
        });
        var pts = new THREE.Points(geom, mat);
        pts.userData.baseSize         = baseSize;
        pts.userData.baseOpacity      = baseOpacity;
        pts.userData.isLayeredScan    = true;
        pts.userData.layerPhase       = (idx / N) * Math.PI * 2;
        /* Per-layer drift — slow position + rotation oscillation so each
           scan feels like it's gently floating in its own current rather
           than locked to the world. Random freqs/phases per axis keep
           the three layers from drifting in lockstep. */
        pts.userData.driftFreq        = 0.06 + Math.random() * 0.08;
        pts.userData.driftPhaseX      = Math.random() * Math.PI * 2;
        pts.userData.driftPhaseY      = Math.random() * Math.PI * 2;
        pts.userData.driftPhaseR      = Math.random() * Math.PI * 2;
        pts.userData.driftAmpX        = 0.05 + Math.random() * 0.04;
        pts.userData.driftAmpY        = 0.04 + Math.random() * 0.03;
        pts.userData.driftAmpRot      = 0.04 + Math.random() * 0.03;
        /* Per-layer flicker — brief opacity blip every few seconds. Adds
           scan-corruption character without the heavy global glitch. */
        pts.userData.flickerNextAt    = 1.5 + Math.random() * 4.0;
        pts.userData.flickerUntil     = 0;
        group.add(pts);
      });
      try { window.dispatchEvent(new CustomEvent('bios:pointcloud-loaded')); } catch (_) {}
    }

    function addCloud(data) {
      var geom = new THREE.BufferGeometry();
      geom.setAttribute('position', new THREE.BufferAttribute(data.positions, 3));
      /* Explicit bounding sphere — Three.js can frustum-cull the cloud
         on frame 1 if this isn't set before the first render. The wave
         plane spans up to ±9 on Z plus breath travel, so r=12 is safe. */
      geom.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 12);

      /* Per-point FLOW displacement — the engine of the morphing form.
         Each vertex stores a random unit-direction vector plus its
         own frequency and phase. Each frame we shift the vertex along
         that direction by sin(time * freq + phase) * amp, so the
         collective produces a slowly writhing surface (think wind on
         a fabric, or a body shifting under cloth). Big amplitudes
         (~0.25) so the form genuinely DEFORMS, not just shimmers.

         Storage layout per vertex: [dirX, dirY, dirZ, freq, phase].
         dirX/Y/Z are a unit vector sampled from a normal distribution
         then normalized — gives isotropic directions. */
      var N = data.positions.length / 3;
      var basePositions = new Float32Array(data.positions);
      var breath = new Float32Array(N * 5);
      for (var bi = 0; bi < N; bi++) {
        /* Random unit direction via gaussian (Marsaglia method) */
        var u1, u2, s;
        do {
          u1 = Math.random() * 2 - 1;
          u2 = Math.random() * 2 - 1;
          s  = u1 * u1 + u2 * u2;
        } while (s >= 1 || s === 0);
        var f = Math.sqrt(-2 * Math.log(s) / s);
        var u3 = Math.random() * 2 - 1;   /* uniform for the Z component */
        var inv = 1 / Math.sqrt(u1 * u1 * f * f + u2 * u2 * f * f + u3 * u3 + 1e-9);
        breath[bi * 5 + 0] = u1 * f * inv;
        breath[bi * 5 + 1] = u2 * f * inv;
        breath[bi * 5 + 2] = u3     * inv;
        breath[bi * 5 + 3] = 0.06 + Math.random() * 0.16;   /* 0.06–0.22 Hz */
        breath[bi * 5 + 4] = Math.random() * Math.PI * 2;
      }
      geom.userData = {
        basePositions: basePositions,
        breath:        breath,
        workArr:       data.positions,  /* the live buffer the GPU reads */
        shapes:        data.shapes      /* array of N*3 Float32Arrays, one per shape variant (optional) */
      };

      /* Soft-sprite + additive-blending look: each vertex renders as a
         radial-gradient blob instead of a hard square pixel, so 45k
         overlapping points stack into a luminous amber fog rather than
         scattered dust. Fixed pixel size (sizeAttenuation: false) keeps
         density consistent regardless of camera depth. */
      /* Tiny dense grey points — match Eastern Market reference's
         scan-render look. Each dot is a sub-pixel speck, and the
         density of the cloud (110k+ points) is what gives the form
         its readable silhouette. Slightly raised opacity so individual
         dots stay legible at this small size. */
      var baseSize    = 1.2;
      var baseOpacity = 0.92;
      var mat = new THREE.PointsMaterial({
        color: 0xe6e2d8,                /* near-white warm grey */
        size: baseSize,
        sizeAttenuation: false,
        transparent: true,
        opacity: baseOpacity,
        depthWrite: false,
        depthTest: false
      });
      var pts = new THREE.Points(geom, mat);
      pts.userData.baseSize    = baseSize;
      pts.userData.baseOpacity = baseOpacity;
      pts.userData.isBreatherSource = true;
      group.add(pts);

      /* Optional wires / glyphs from earlier scenes — still rendered if
         their generator returns them. The current default
         (procMorphingForm) returns neither, so these blocks become
         no-ops. Kept in place so swapping placeholderSphere back to
         procWiredScene still works. */
      if (data.linePositions && data.linePositions.length >= 6) {
        var lineGeom = new THREE.BufferGeometry();
        lineGeom.setAttribute('position', new THREE.BufferAttribute(data.linePositions, 3));
        lineGeom.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 12);
        var lineMat = new THREE.LineBasicMaterial({
          color: 0xbeb9aa,
          transparent: true,
          opacity: 0.18,
          depthWrite: false,
          depthTest: false,
          blending: THREE.AdditiveBlending
        });
        group.add(new THREE.LineSegments(lineGeom, lineMat));
      }
      if (data.glyphs && data.glyphs.length) {
        var glyphTextureCache = {};
        function getGlyphTexture(ch) {
          if (glyphTextureCache[ch]) return glyphTextureCache[ch];
          var SZ = 128;
          var cnv = document.createElement('canvas');
          cnv.width = cnv.height = SZ;
          var cctx = cnv.getContext('2d');
          cctx.clearRect(0, 0, SZ, SZ);
          cctx.font = 'bold 88px JetBrains Mono, Menlo, Consolas, monospace';
          cctx.fillStyle = 'rgba(230, 224, 208, 1)';
          cctx.textAlign = 'center';
          cctx.textBaseline = 'middle';
          cctx.fillText(ch, SZ / 2, SZ / 2 + 4);
          var t = new THREE.CanvasTexture(cnv);
          t.minFilter = THREE.LinearFilter;
          t.magFilter = THREE.LinearFilter;
          glyphTextureCache[ch] = t;
          return t;
        }
        for (var g = 0; g < data.glyphs.length; g++) {
          var gd = data.glyphs[g];
          var gmat = new THREE.SpriteMaterial({
            map:          getGlyphTexture(gd.ch),
            color:        0xddd5c5,
            transparent:  true,
            opacity:      gd.opacity,
            depthWrite:   false,
            depthTest:    false,
            blending:     THREE.AdditiveBlending
          });
          var gsprite = new THREE.Sprite(gmat);
          gsprite.position.set(gd.x, gd.y, gd.z);
          gsprite.scale.set(gd.scale, gd.scale, 1);
          group.add(gsprite);
        }
      }
      try { window.dispatchEvent(new CustomEvent('bios:pointcloud-loaded')); } catch (_) {}

      function makePointSprite() {
        var SZ = 64;
        var cnv = document.createElement('canvas');
        cnv.width = cnv.height = SZ;
        var ctx = cnv.getContext('2d');
        var grad = ctx.createRadialGradient(SZ / 2, SZ / 2, 0, SZ / 2, SZ / 2, SZ / 2);
        grad.addColorStop(0.00, 'rgba(255,255,255,1)');
        grad.addColorStop(0.25, 'rgba(255,255,255,0.55)');
        grad.addColorStop(0.55, 'rgba(255,255,255,0.18)');
        grad.addColorStop(1.00, 'rgba(255,255,255,0)');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, SZ, SZ);
        var tex = new THREE.CanvasTexture(cnv);
        tex.minFilter = THREE.LinearFilter;
        tex.magFilter = THREE.LinearFilter;
        return tex;
      }
      try { window.dispatchEvent(new CustomEvent('bios:pointcloud-loaded')); } catch (_) {}
    }
  }

  /* ── PLY parser ─────────────────────────────────────────────────────────
     Handles ASCII and binary_little_endian PLY files. Polycam exports
     binary by default; ASCII is here for hand-edited or third-party scans.

     We only extract x/y/z. Vertex colors are intentionally discarded so
     every cloud renders pure white regardless of the scanned material. */
  function parsePLY(buf) {
    var u8 = new Uint8Array(buf);

    /* Find the end of the header (text portion) by scanning for "end_header\n". */
    var marker = 'end_header';
    var headerEnd = -1;
    var headerStr = '';
    var scanLimit = Math.min(u8.length, 8192);
    for (var i = 0; i < scanLimit; i++) {
      headerStr += String.fromCharCode(u8[i]);
      if (headerStr.endsWith(marker)) { headerEnd = i + 1; break; }
    }
    if (headerEnd < 0) throw new Error('invalid PLY header');
    /* Skip the trailing newline after end_header */
    while (headerEnd < u8.length && (u8[headerEnd] === 10 || u8[headerEnd] === 13)) headerEnd++;

    var format = 'ascii';
    var vertexCount = 0;
    var properties = [];
    headerStr.split('\n').forEach(function (line) {
      var p = line.trim().split(/\s+/);
      if (p[0] === 'format') format = p[1];
      else if (p[0] === 'element' && p[1] === 'vertex') vertexCount = parseInt(p[2], 10);
      else if (p[0] === 'property' && p[2]) properties.push({ type: p[1], name: p[2] });
    });

    if (!vertexCount) throw new Error('no vertices');

    var positions = new Float32Array(vertexCount * 3);

    if (format === 'ascii') {
      var bodyStr = new TextDecoder().decode(u8.subarray(headerEnd));
      var lines = bodyStr.split('\n');
      var xi = properties.findIndex(function (p) { return p.name === 'x'; });
      var yi = properties.findIndex(function (p) { return p.name === 'y'; });
      var zi = properties.findIndex(function (p) { return p.name === 'z'; });
      for (var v = 0; v < vertexCount; v++) {
        var parts = lines[v].trim().split(/\s+/);
        positions[v * 3]     = parseFloat(parts[xi]);
        positions[v * 3 + 1] = parseFloat(parts[yi]);
        positions[v * 3 + 2] = parseFloat(parts[zi]);
      }
    } else if (format === 'binary_little_endian') {
      var dv = new DataView(buf, headerEnd);
      /* Compute byte offsets for each property within one vertex record */
      var offset = 0;
      var byName = {};
      properties.forEach(function (p) {
        byName[p.name] = { type: p.type, off: offset };
        offset += sizeOf(p.type);
      });
      var stride = offset;
      var ox = byName.x.off, oy = byName.y.off, oz = byName.z.off;
      for (var w = 0; w < vertexCount; w++) {
        var base = w * stride;
        positions[w * 3]     = dv.getFloat32(base + ox, true);
        positions[w * 3 + 1] = dv.getFloat32(base + oy, true);
        positions[w * 3 + 2] = dv.getFloat32(base + oz, true);
      }
    } else {
      throw new Error('unsupported PLY format: ' + format);
    }

    return normalize(positions);
  }

  function sizeOf(t) {
    switch (t) {
      case 'float': case 'float32': return 4;
      case 'double': case 'float64': return 8;
      case 'uchar': case 'uint8': return 1;
      case 'char': case 'int8': return 1;
      case 'ushort': case 'uint16': return 2;
      case 'short': case 'int16': return 2;
      case 'uint': case 'uint32': return 4;
      case 'int': case 'int32': return 4;
      default: return 4;
    }
  }

  /* Cap the vertex count so a too-large scan doesn't lock the main thread
     parsing or drag fps. 60K points is more than enough density for a
     portal background — beyond that the eye can't see the difference and
     we just pay GPU. Even-stride sampling keeps the silhouette intact. */
  var MAX_POINTS = 60000;
  function decimate(positions) {
    var count = positions.length / 3;
    if (count <= MAX_POINTS) return positions;
    var stride = Math.ceil(count / MAX_POINTS);
    var out = new Float32Array(Math.ceil(count / stride) * 3);
    var j = 0;
    for (var i = 0; i < count; i += stride) {
      out[j * 3]     = positions[i * 3];
      out[j * 3 + 1] = positions[i * 3 + 1];
      out[j * 3 + 2] = positions[i * 3 + 2];
      j++;
    }
    return out.subarray(0, j * 3);
  }

  /* Center the cloud on its bounding-box midpoint and scale so the largest
     dimension fits into a ~3-unit cube. Photogrammetry scans come out at
     arbitrary world scales (sometimes 0.1m units, sometimes 1.0); this
     normalizes regardless of how Polycam exported. */
  function normalize(positions) {
    positions = decimate(positions);
    var minX = Infinity, maxX = -Infinity,
        minY = Infinity, maxY = -Infinity,
        minZ = Infinity, maxZ = -Infinity;
    for (var i = 0; i < positions.length; i += 3) {
      var x = positions[i], y = positions[i + 1], z = positions[i + 2];
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
      if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
    }
    var cx = (minX + maxX) / 2;
    var cy = (minY + maxY) / 2;
    var cz = (minZ + maxZ) / 2;
    var span = Math.max(maxX - minX, maxY - minY, maxZ - minZ);
    /* Was 3.0 / span — that stretched the cloud to ~75% of the viewport
       width, leaving the points so sparse they looked like dust. 1.8
       packs the cloud into a tighter central blob (~45% viewport),
       letting overlapping soft sprites stack into a visibly glowing
       fog under additive blending. */
    var scale = span > 0 ? 1.8 / span : 1;
    for (var j = 0; j < positions.length; j += 3) {
      positions[j]     = (positions[j]     - cx) * scale;
      positions[j + 1] = (positions[j + 1] - cy) * scale;
      positions[j + 2] = (positions[j + 2] - cz) * scale;
    }
    return { positions: positions };
  }

  /* ── GLB parser ─────────────────────────────────────────────────────────
     glTF 2.0 binary container. Layout:

       Bytes  Content
       0..3   Magic 'glTF' (0x46546C67)
       4..7   Version (uint32 LE, should be 2)
       8..11  Total length (uint32 LE)
       12..15 Chunk 0 length
       16..19 Chunk 0 type ('JSON' = 0x4E4F534A)
       20..   Chunk 0 data (JSON describing accessors / bufferViews)
       N+0..3 Chunk 1 length
       N+4..7 Chunk 1 type ('BIN\0' = 0x004E4942)
       N+8..  Chunk 1 data (binary buffer containing vertex floats)

     We walk the JSON to find the first mesh's POSITION accessor, look up
     its bufferView to get a byte offset + length inside the BIN chunk,
     then read N float32s out of there. Faces are ignored — we render the
     vertices as a point cloud regardless of whether the source was a mesh
     or already a point cloud. */
  function parseGLB(buf) {
    var dv = new DataView(buf);
    if (dv.getUint32(0, true) !== 0x46546C67) throw new Error('not GLB');

    /* Chunk 0 = JSON */
    var jsonLen = dv.getUint32(12, true);
    var jsonStr = new TextDecoder().decode(new Uint8Array(buf, 20, jsonLen));
    var gltf = JSON.parse(jsonStr);

    /* Chunk 1 = binary buffer */
    var binStart = 20 + jsonLen + 8;

    /* Find the first POSITION accessor across all meshes. */
    var posAccessor = null;
    if (gltf.meshes) {
      for (var m = 0; m < gltf.meshes.length && !posAccessor; m++) {
        var prims = gltf.meshes[m].primitives || [];
        for (var p = 0; p < prims.length && !posAccessor; p++) {
          var attr = prims[p].attributes || {};
          if (typeof attr.POSITION === 'number') {
            posAccessor = gltf.accessors[attr.POSITION];
          }
        }
      }
    }
    if (!posAccessor) throw new Error('no POSITION attribute');
    /* componentType 5126 = FLOAT, type VEC3 — what Polycam outputs */
    if (posAccessor.componentType !== 5126 || posAccessor.type !== 'VEC3') {
      throw new Error('unexpected POSITION format');
    }

    var bv = gltf.bufferViews[posAccessor.bufferView];
    var offset = binStart + (bv.byteOffset || 0) + (posAccessor.byteOffset || 0);
    var count  = posAccessor.count;
    /* Read into a fresh Float32Array (sliced — the original buffer may have
       padding alignment issues if we tried to view it directly). */
    var positions = new Float32Array(count * 3);
    for (var i = 0; i < count * 3; i++) {
      positions[i] = dv.getFloat32(offset + i * 4, true);
    }

    return normalize(positions);
  }

  /* ── GLTF (text + companion .bin) loader ────────────────────────────────
     Polycam's free tier only exports the two-file glTF format (not GLB).
     The .gltf file is JSON describing the scene; the .bin file holds the
     binary vertex data. We fetch both, walk the JSON to find the first
     mesh's POSITION accessor, then pull float32 vertices out of the .bin.

     The user MUST upload both files. The .gltf will normally have a
     "buffers": [{"uri": "scene.bin"}] entry referencing the .bin by its
     filename. We ignore that URI (Shopify renames assets and adds cache
     busters) and use the explicit .bin URL passed in by Liquid. */
  function loadGLTFPair(gltfUrl, binUrl) {
    if (!binUrl) return Promise.reject(new Error('no bin URL'));
    return Promise.all([
      fetch(gltfUrl, { credentials: 'omit' }).then(function (r) {
        if (!r.ok) throw new Error('gltf not ok'); return r.json();
      }),
      fetch(binUrl, { credentials: 'omit' }).then(function (r) {
        if (!r.ok) throw new Error('bin not ok'); return r.arrayBuffer();
      })
    ]).then(function (parts) {
      return extractPositionsFromGLTF(parts[0], parts[1]);
    });
  }

  function extractPositionsFromGLTF(gltf, binBuf) {
    var posAccessor = null;
    if (gltf.meshes) {
      for (var m = 0; m < gltf.meshes.length && !posAccessor; m++) {
        var prims = gltf.meshes[m].primitives || [];
        for (var p = 0; p < prims.length && !posAccessor; p++) {
          var attr = prims[p].attributes || {};
          if (typeof attr.POSITION === 'number') {
            posAccessor = gltf.accessors[attr.POSITION];
          }
        }
      }
    }
    if (!posAccessor) throw new Error('no POSITION attribute');
    if (posAccessor.componentType !== 5126 || posAccessor.type !== 'VEC3') {
      throw new Error('unexpected POSITION format');
    }
    var bv = gltf.bufferViews[posAccessor.bufferView];
    var offset = (bv.byteOffset || 0) + (posAccessor.byteOffset || 0);
    var count  = posAccessor.count;
    var dv = new DataView(binBuf);
    var positions = new Float32Array(count * 3);
    for (var i = 0; i < count * 3; i++) {
      positions[i] = dv.getFloat32(offset + i * 4, true);
    }
    return normalize(positions);
  }

  /* ── Procedural placeholders ──────────────────────────────────────────
     Picks one of three abstract generators at random per page load —
     each is itself randomly parameterised, so every refresh shows a
     genuinely different shape. The three styles balance each other:
        - waves: undulating surface, organic ripple feel
        - clouds: 5-8 gaussian blobs scattered in space, soft + drifty
        - aizawa: strange attractor (kept as the "code/system" option)
     Each returns { positions: Float32Array } in the same shape as a
     parsed scan. */
  function placeholderSphere() {
    /* Default to procMorphingForm — a single dense morphing shape that
       continuously deforms via 3D flow displacement, inspired by the
       Eastern Market landing page (a video-driven scanned figure
       rendered as particles). One coherent form that warps in space,
       very dense, monochrome grey-on-black. No glyphs, no wires —
       just the morphing shape. */
    return procMorphingForm();

    /* Alternatives — switch placeholderSphere to one of these to lock
       a specific style:
         return procMorphingForm();
         return procWiredScene();
         return procNetworkNodes();
         return procGeometricShapes();
         return procWavesTopDown();
    */
  }

  /* ── Morphing form ──────────────────────────────────────────────────
     A dense (~110k points) figurative form composed of 6 "body part"
     blobs (head + torso + 2 arms + 2 legs) that morph between 4 pose
     variants. Each vertex is permanently assigned to ONE blob and a
     local uniform position inside that blob's unit sphere; per pose,
     the blob's center and per-axis scale change. So a vertex that
     belongs to the "head" stays in the head across all poses — the
     pose-blend reads as a coherent figure changing posture rather
     than geometry teleporting.

     Inspired by the Eastern Market landing page (a 3D-scanned figure
     rendered as particles): asymmetric, body-like silhouettes that
     flow between recognizable poses. */
  function procMorphingForm() {
    var N = 110000;
    var positions = new Float32Array(N * 3);
    var SHAPE_COUNT = 4;
    var shapes = [];
    for (var si = 0; si < SHAPE_COUNT; si++) shapes.push(new Float32Array(N * 3));

    /* Each pose is an array of 6 blob descriptors at matching indices:
       [head, torso, L-arm, R-arm, L-leg, R-leg]
       cx/cy/cz: blob center
       sx/sy/sz: per-axis scale (size of the blob along each axis)
       w:       relative point weight (volume-ish, used for distribution)
       Asymmetry is built in by making L vs R blobs differ slightly
       across poses — real bodies in motion aren't symmetric. */
    var poses = [
      /* Pose 0 — STANDING upright, slight forward tilt of arms */
      [
        { cx:  0.00, cy:  1.05, cz:  0.05, sx: 0.28, sy: 0.30, sz: 0.28, w: 1.0 },
        { cx:  0.00, cy:  0.25, cz:  0.00, sx: 0.45, sy: 0.65, sz: 0.32, w: 4.5 },
        { cx: -0.55, cy:  0.30, cz:  0.10, sx: 0.16, sy: 0.55, sz: 0.16, w: 1.6 },
        { cx:  0.55, cy:  0.20, cz: -0.05, sx: 0.16, sy: 0.55, sz: 0.16, w: 1.6 },
        { cx: -0.22, cy: -0.70, cz:  0.00, sx: 0.20, sy: 0.65, sz: 0.20, w: 2.4 },
        { cx:  0.22, cy: -0.70, cz:  0.00, sx: 0.20, sy: 0.65, sz: 0.20, w: 2.4 }
      ],
      /* Pose 1 — REACHING: torso forward, one arm extended high, the other
         low. X coords centered on origin so the form's weighted centroid
         lands at viewport center rather than drifting right. */
      [
        { cx:  0.05, cy:  0.85, cz:  0.30, sx: 0.27, sy: 0.28, sz: 0.27, w: 1.0 },
        { cx: -0.10, cy:  0.10, cz:  0.20, sx: 0.50, sy: 0.55, sz: 0.40, w: 4.5 },
        { cx: -0.45, cy:  0.95, cz:  0.55, sx: 0.14, sy: 0.55, sz: 0.14, w: 1.6 },
        { cx:  0.50, cy: -0.10, cz:  0.10, sx: 0.14, sy: 0.55, sz: 0.14, w: 1.6 },
        { cx: -0.40, cy: -0.65, cz: -0.05, sx: 0.20, sy: 0.65, sz: 0.20, w: 2.4 },
        { cx:  0.15, cy: -0.75, cz:  0.05, sx: 0.20, sy: 0.65, sz: 0.20, w: 2.4 }
      ],
      /* Pose 2 — CROUCHING: head lower, limbs folded under torso */
      [
        { cx:  0.05, cy:  0.55, cz:  0.20, sx: 0.27, sy: 0.28, sz: 0.27, w: 1.0 },
        { cx:  0.00, cy:  0.10, cz:  0.10, sx: 0.55, sy: 0.42, sz: 0.40, w: 4.5 },
        { cx: -0.45, cy: -0.05, cz:  0.30, sx: 0.18, sy: 0.40, sz: 0.18, w: 1.6 },
        { cx:  0.45, cy: -0.05, cz:  0.30, sx: 0.18, sy: 0.40, sz: 0.18, w: 1.6 },
        { cx: -0.25, cy: -0.45, cz:  0.40, sx: 0.22, sy: 0.45, sz: 0.30, w: 2.4 },
        { cx:  0.25, cy: -0.45, cz:  0.40, sx: 0.22, sy: 0.45, sz: 0.30, w: 2.4 }
      ],
      /* Pose 3 — LYING/curled: everything closer to horizontal, asymmetric.
         Re-balanced so the centroid lands at origin — head extends left,
         legs extend right, but the per-blob centers sum to ~0. */
      [
        { cx: -0.95, cy: -0.15, cz:  0.00, sx: 0.28, sy: 0.28, sz: 0.28, w: 1.0 },
        { cx: -0.20, cy: -0.10, cz:  0.00, sx: 0.70, sy: 0.30, sz: 0.45, w: 4.5 },
        { cx: -0.45, cy:  0.25, cz: -0.20, sx: 0.50, sy: 0.16, sz: 0.16, w: 1.6 },
        { cx:  0.20, cy:  0.20, cz:  0.10, sx: 0.50, sy: 0.16, sz: 0.16, w: 1.6 },
        { cx:  0.55, cy: -0.25, cz: -0.10, sx: 0.60, sy: 0.22, sz: 0.22, w: 2.4 },
        { cx:  0.60, cy: -0.45, cz:  0.20, sx: 0.55, sy: 0.22, sz: 0.22, w: 2.4 }
      ]
    ];

    /* Build cumulative weight table (any pose's weights work — same
       weights across poses since blob ordering is consistent). */
    var weights = poses[0].map(function (b) { return b.w; });
    var totalW  = 0;
    for (var ww = 0; ww < weights.length; ww++) totalW += weights[ww];
    var cum = []; var acc = 0;
    for (var cw = 0; cw < weights.length; cw++) { acc += weights[cw]; cum.push(acc); }

    /* Per-point random offsets baked once, plus a small noise term so
       blobs don't read as pure ellipsoids. */
    for (var i = 0; i < N; i++) {
      /* Pick blob by weight */
      var pick = Math.random() * totalW;
      var bi = 0;
      for (var bk = 0; bk < cum.length; bk++) { if (pick <= cum[bk]) { bi = bk; break; } }

      /* Uniform random direction + cube-root for uniform volume sampling
         inside a unit ball. */
      var dx, dy, dz, ll;
      do {
        dx = Math.random() * 2 - 1;
        dy = Math.random() * 2 - 1;
        dz = Math.random() * 2 - 1;
        ll = dx * dx + dy * dy + dz * dz;
      } while (ll > 1 || ll === 0);
      var rscale = Math.cbrt(Math.random()) / Math.sqrt(ll);
      var lx = dx * rscale;
      var ly = dy * rscale;
      var lz = dz * rscale;

      /* Small "organic noise" offset that breaks perfect ellipsoid
         surfaces — different per vertex but consistent across poses,
         so the surface texture stays attached to its point. */
      var noiseX = (Math.random() - 0.5) * 0.06;
      var noiseY = (Math.random() - 0.5) * 0.06;
      var noiseZ = (Math.random() - 0.5) * 0.06;

      var i3 = i * 3;
      for (var p = 0; p < SHAPE_COUNT; p++) {
        var blob = poses[p][bi];
        shapes[p][i3]     = blob.cx + lx * blob.sx + noiseX;
        shapes[p][i3 + 1] = blob.cy + ly * blob.sy + noiseY;
        shapes[p][i3 + 2] = blob.cz + lz * blob.sz + noiseZ;
      }

      /* Initial = first pose. tick() overwrites on frame 1 with the
         weighted blend. */
      positions[i3]     = shapes[0][i3];
      positions[i3 + 1] = shapes[0][i3 + 1];
      positions[i3 + 2] = shapes[0][i3 + 2];
    }

    return { positions: positions, shapes: shapes };
  }

  /* ── Wired (Lain) ───────────────────────────────────────────────────
     Sparse nodes interconnected by a dense web of straight "telephone-
     wire" line segments, salted with floating numeric/hex glyphs that
     act as data debris in the digital space. Each node still has its
     own little cluster of dots; the wires are LineSegments; the glyphs
     are returned as a separate array for the caller to instantiate as
     billboarded Sprites. */
  function procWiredScene() {
    var NODE_COUNT      = 80;
    var POINTS_PER_NODE = 16;
    var DISC_R          = 3.6;
    var Y_SPREAD        = 0.55;   /* wider Y range for 3D depth */

    /* Each node picks more neighbors than the previous network — this
       makes the "Wired" feel densely cabled rather than constellated. */
    var CONNECT_DIST = 1.6;
    var MAX_LINKS    = 5;

    var totalPoints = NODE_COUNT * POINTS_PER_NODE;
    var positions   = new Float32Array(totalPoints * 3);
    var centers     = new Float32Array(NODE_COUNT * 3);

    for (var i = 0; i < NODE_COUNT; i++) {
      var cx, cz;
      do {
        cx = (Math.random() * 2 - 1) * DISC_R;
        cz = (Math.random() * 2 - 1) * DISC_R;
      } while (cx * cx + cz * cz > DISC_R * DISC_R);
      var cy = (Math.random() - 0.5) * Y_SPREAD;
      centers[i * 3]     = cx;
      centers[i * 3 + 1] = cy;
      centers[i * 3 + 2] = cz;

      var base = i * POINTS_PER_NODE;
      for (var p = 0; p < POINTS_PER_NODE; p++) {
        var r = Math.sqrt(Math.random()) * 0.04;
        var a = Math.random() * Math.PI * 2;
        var idx = (base + p) * 3;
        positions[idx]     = cx + Math.cos(a) * r;
        positions[idx + 1] = cy + (Math.random() - 0.5) * 0.025;
        positions[idx + 2] = cz + Math.sin(a) * r;
      }
    }

    /* Wires: nearest-neighbor edges (no duplicates by index ordering). */
    var linePoints = [];
    for (var i2 = 0; i2 < NODE_COUNT; i2++) {
      var dists = [];
      for (var j = 0; j < NODE_COUNT; j++) {
        if (j === i2) continue;
        var dx = centers[i2 * 3]     - centers[j * 3];
        var dy = centers[i2 * 3 + 1] - centers[j * 3 + 1];
        var dz = centers[i2 * 3 + 2] - centers[j * 3 + 2];
        var d  = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (d < CONNECT_DIST) dists.push({ j: j, d: d });
      }
      dists.sort(function (a, b) { return a.d - b.d; });
      var take = Math.min(dists.length, MAX_LINKS);
      for (var k = 0; k < take; k++) {
        var jj = dists[k].j;
        if (jj <= i2) continue;
        linePoints.push(
          centers[i2 * 3], centers[i2 * 3 + 1], centers[i2 * 3 + 2],
          centers[jj * 3], centers[jj * 3 + 1], centers[jj * 3 + 2]
        );
      }
    }

    /* Floating glyphs — single-char text sprites scattered throughout
       the space like data fragments. Mix of digits, hex letters, and
       a few code-flavored symbols. Many small ones, occasional larger
       ones. */
    var GLYPH_COUNT = 64;
    var glyphChars  = '0123456789ABCDEFabcdef.:/-=+*<>';
    var glyphs = [];
    for (var g = 0; g < GLYPH_COUNT; g++) {
      var gx, gz;
      do {
        gx = (Math.random() * 2 - 1) * DISC_R;
        gz = (Math.random() * 2 - 1) * DISC_R;
      } while (gx * gx + gz * gz > DISC_R * DISC_R);
      var gy = (Math.random() - 0.5) * Y_SPREAD;
      glyphs.push({
        x:       gx,
        y:       gy,
        z:       gz,
        ch:      glyphChars.charAt(Math.floor(Math.random() * glyphChars.length)),
        scale:   0.06 + Math.random() * 0.10,
        opacity: 0.35 + Math.random() * 0.45
      });
    }
    /* A handful of multi-char "code strings" — short hex words/IDs
       that read as system identifiers in the Wired. */
    var WORD_COUNT = 12;
    for (var w = 0; w < WORD_COUNT; w++) {
      var len = 4 + Math.floor(Math.random() * 5);   /* 4–8 chars */
      var word = '';
      for (var c = 0; c < len; c++) {
        word += glyphChars.charAt(Math.floor(Math.random() * glyphChars.length));
      }
      var wx, wz;
      do {
        wx = (Math.random() * 2 - 1) * DISC_R;
        wz = (Math.random() * 2 - 1) * DISC_R;
      } while (wx * wx + wz * wz > DISC_R * DISC_R);
      var wy = (Math.random() - 0.5) * Y_SPREAD;
      /* Lay each char along a small horizontal offset so the word
         reads left-to-right when rendered as billboards. */
      var charSpacing = 0.07;
      for (var cc = 0; cc < len; cc++) {
        glyphs.push({
          x:       wx + (cc - (len - 1) / 2) * charSpacing,
          y:       wy,
          z:       wz,
          ch:      word.charAt(cc),
          scale:   0.07,
          opacity: 0.55
        });
      }
    }

    return {
      positions:     positions,
      linePositions: new Float32Array(linePoints),
      glyphs:        glyphs
    };
  }

  /* ── Network nodes ───────────────────────────────────────────────────
     Sparse field of small grey "nodes", each a tight cluster of points,
     scattered across a near-top-down disc. Computes nearest-neighbor
     edges and returns them as line-segment positions so the caller can
     draw faint connecting lines. Reads as a constellation / circuit
     map seen from above — quiet, structured, no clustering or wave
     pattern. */
  function procNetworkNodes() {
    var NODE_COUNT      = 95;
    var POINTS_PER_NODE = 22;
    var DISC_R          = 3.3;
    var CONNECT_DIST    = 1.15;
    var MAX_LINKS       = 3;

    var totalPoints = NODE_COUNT * POINTS_PER_NODE;
    var positions   = new Float32Array(totalPoints * 3);
    var centers     = new Float32Array(NODE_COUNT * 3);

    /* Place node centers uniformly inside a disc, with a small Y offset
       per node so the network reads as having a hint of depth when
       parallax rocks the camera. */
    for (var i = 0; i < NODE_COUNT; i++) {
      var cx, cz;
      do {
        cx = (Math.random() * 2 - 1) * DISC_R;
        cz = (Math.random() * 2 - 1) * DISC_R;
      } while (cx * cx + cz * cz > DISC_R * DISC_R);
      var cy = (Math.random() - 0.5) * 0.18;
      centers[i * 3]     = cx;
      centers[i * 3 + 1] = cy;
      centers[i * 3 + 2] = cz;

      /* Tight cluster of points around the center — gives the node
         visual mass without making it a perfect circle. Radius small
         enough that the cluster reads as a single dot from camera
         distance. */
      var base = i * POINTS_PER_NODE;
      for (var p = 0; p < POINTS_PER_NODE; p++) {
        var r = Math.sqrt(Math.random()) * 0.045;
        var a = Math.random() * Math.PI * 2;
        var idx = (base + p) * 3;
        positions[idx]     = cx + Math.cos(a) * r;
        positions[idx + 1] = cy + (Math.random() - 0.5) * 0.015;
        positions[idx + 2] = cz + Math.sin(a) * r;
      }
    }

    /* Build line segments to up to MAX_LINKS nearest neighbors within
       CONNECT_DIST. Use index ordering to avoid duplicate segments. */
    var linePoints = [];
    for (var i2 = 0; i2 < NODE_COUNT; i2++) {
      var dists = [];
      for (var j = 0; j < NODE_COUNT; j++) {
        if (j === i2) continue;
        var dx = centers[i2 * 3]     - centers[j * 3];
        var dy = centers[i2 * 3 + 1] - centers[j * 3 + 1];
        var dz = centers[i2 * 3 + 2] - centers[j * 3 + 2];
        var d  = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (d < CONNECT_DIST) dists.push({ j: j, d: d });
      }
      dists.sort(function (a, b) { return a.d - b.d; });
      var take = Math.min(dists.length, MAX_LINKS);
      for (var k = 0; k < take; k++) {
        var jj = dists[k].j;
        if (jj <= i2) continue;   /* skip dupes, only emit each edge once */
        linePoints.push(
          centers[i2 * 3], centers[i2 * 3 + 1], centers[i2 * 3 + 2],
          centers[jj * 3], centers[jj * 3 + 1], centers[jj * 3 + 2]
        );
      }
    }

    return {
      positions:     positions,
      linePositions: new Float32Array(linePoints)
    };
  }

  /* ── Geometric shapes scatter ─────────────────────────────────────────
     Generates ~240 small geometric outlines (squares, triangles, etc.)
     scattered uniformly inside a disc on the X/Z plane. Each shape is
     placed at a random center, given a random rotation and scale, and
     drawn as a string of points along its outline. The collective
     reads as "many shapes forming a larger composition", viewed from
     directly above when paired with the steep group rotation in
     tick().

     Local axes after the near-top-down group rotation:
       local X → screen X     (lateral position)
       local Y → screen depth (small Y variance reads as subtle layering)
       local Z → screen Y     (vertical position on screen)

     So shape outlines drawn on the X/Z plane appear correctly oriented
     when viewed from the camera; small Y offsets per shape give a hint
     of depth without breaking the top-down read. */
  function procGeometricShapes() {
    var SHAPE_COUNT = 240;
    var TARGET_TOTAL = 70000;
    var avgPerShape = TARGET_TOTAL / SHAPE_COUNT;
    var DISC_R = 3.2;                   /* composition radius */

    /* Shape-stamp generators. Each returns an array of [x, y, z]
       triples describing the outline relative to the shape's local
       origin, at the given scale and rotation. All shapes lie on
       y = 0 in local space; the caller adds the per-shape y offset. */
    var stamps = [
      /* 0: square outline */
      function (n, scale, rot) {
        var arr = [], per = Math.ceil(n / 4);
        var c = Math.cos(rot), s = Math.sin(rot);
        for (var e = 0; e < 4; e++) {
          for (var i = 0; i < per; i++) {
            var t = (i / per) - 0.5;
            var lx, lz;
            if      (e === 0) { lx = t;    lz = -0.5; }
            else if (e === 1) { lx = 0.5;  lz = t;    }
            else if (e === 2) { lx = -t;   lz = 0.5;  }
            else              { lx = -0.5; lz = -t;   }
            arr.push([(lx * c - lz * s) * scale, 0, (lx * s + lz * c) * scale]);
          }
        }
        return arr;
      },
      /* 1: circle outline */
      function (n, scale, rot) {
        var arr = [];
        for (var i = 0; i < n; i++) {
          var a = (i / n) * Math.PI * 2 + rot;
          arr.push([Math.cos(a) * 0.5 * scale, 0, Math.sin(a) * 0.5 * scale]);
        }
        return arr;
      },
      /* 2: triangle outline */
      function (n, scale, rot) {
        var arr = [], per = Math.ceil(n / 3);
        var v = [];
        for (var k = 0; k < 3; k++) {
          var a = rot + k * (Math.PI * 2 / 3);
          v.push([Math.cos(a) * 0.5, Math.sin(a) * 0.5]);
        }
        for (var e = 0; e < 3; e++) {
          var a0 = v[e], b0 = v[(e + 1) % 3];
          for (var i = 0; i < per; i++) {
            var t = i / per;
            arr.push([(a0[0] + (b0[0] - a0[0]) * t) * scale, 0, (a0[1] + (b0[1] - a0[1]) * t) * scale]);
          }
        }
        return arr;
      },
      /* 3: hexagon outline */
      function (n, scale, rot) {
        var arr = [], per = Math.ceil(n / 6);
        var v = [];
        for (var k = 0; k < 6; k++) {
          var a = rot + k * (Math.PI / 3);
          v.push([Math.cos(a) * 0.5, Math.sin(a) * 0.5]);
        }
        for (var e = 0; e < 6; e++) {
          var a0 = v[e], b0 = v[(e + 1) % 6];
          for (var i = 0; i < per; i++) {
            var t = i / per;
            arr.push([(a0[0] + (b0[0] - a0[0]) * t) * scale, 0, (a0[1] + (b0[1] - a0[1]) * t) * scale]);
          }
        }
        return arr;
      },
      /* 4: cross / plus */
      function (n, scale, rot) {
        var arr = [], half = Math.ceil(n / 2);
        var c = Math.cos(rot), s = Math.sin(rot);
        for (var i = 0; i < half; i++) {
          var t = ((i / half) - 0.5) * scale;
          arr.push([t * c, 0, t * s]);
          arr.push([-t * s, 0, t * c]);
        }
        return arr;
      },
      /* 5: line segment */
      function (n, scale, rot) {
        var arr = [];
        var c = Math.cos(rot), s = Math.sin(rot);
        for (var i = 0; i < n; i++) {
          var t = ((i / n) - 0.5) * scale;
          arr.push([t * c, 0, t * s]);
        }
        return arr;
      },
      /* 6: filled disc (small solid dot) */
      function (n, scale, rot) {
        var arr = [];
        for (var i = 0; i < n; i++) {
          var r = Math.sqrt(Math.random()) * 0.5 * scale;
          var a = Math.random() * Math.PI * 2;
          arr.push([Math.cos(a) * r, 0, Math.sin(a) * r]);
        }
        return arr;
      },
      /* 7: diamond (square rotated 45°) */
      function (n, scale, rot) { return stamps[0](n, scale, rot + Math.PI / 4); },
      /* 8: nested concentric squares */
      function (n, scale, rot) {
        var half = Math.ceil(n / 2);
        return stamps[0](half, scale, rot).concat(stamps[0](n - half, scale * 0.55, rot));
      },
      /* 9: arc segment (half-circle) */
      function (n, scale, rot) {
        var arr = [];
        for (var i = 0; i < n; i++) {
          var a = rot + (i / n) * Math.PI;
          arr.push([Math.cos(a) * 0.5 * scale, 0, Math.sin(a) * 0.5 * scale]);
        }
        return arr;
      }
    ];

    var positions = [];
    for (var s = 0; s < SHAPE_COUNT; s++) {
      /* Uniform random in a disc — gives the larger composition a
         soft circular silhouette so it reads as "looking down at a
         thing" rather than "looking at a grid". */
      var cx, cz;
      do {
        cx = (Math.random() * 2 - 1) * DISC_R;
        cz = (Math.random() * 2 - 1) * DISC_R;
      } while (cx * cx + cz * cz > DISC_R * DISC_R);
      var cy = (Math.random() - 0.5) * 0.22;   /* subtle Y layering for depth */
      var scale = 0.16 + Math.random() * 0.55;
      var rot   = Math.random() * Math.PI * 2;
      var npts  = Math.max(40, Math.floor(avgPerShape + (Math.random() - 0.5) * 160));
      var gen   = stamps[Math.floor(Math.random() * stamps.length)];
      var local = gen(npts, scale, rot);
      for (var p = 0; p < local.length; p++) {
        positions.push(cx + local[p][0]);
        positions.push(cy + local[p][1]);
        positions.push(cz + local[p][2]);
      }
    }
    return { positions: new Float32Array(positions) };
  }

  /* ── Top-down ocean waves ─────────────────────────────────────────────
     A wide flat plane of points spanning the viewport, with interfering
     sine waves giving the Y axis a rippled "ocean surface" feel. Random
     params per load so every refresh looks different. The wave field is
     deliberately NOT centrally clustered — points are spread evenly
     across the X/Z plane so the surface fills the screen edge-to-edge
     when viewed from above (group rotation tilts it down in tick()).

     We bake one dominant wave direction (the "swell" rolling in) plus
     2–3 weaker cross-waves at random angles for the secondary ripples
     that real ocean surfaces have. A small per-point Y jitter keeps
     the surface from looking like a single sheet of paper. */
  function procWavesTopDown() {
    var N = 70000;
    var positions = new Float32Array(N * 3);
    /* Wide on X, even deeper on Z — the depth axis recedes into the
       distance under the forward tilt, so we want a long runway of
       waves to fill the perspective horizon. EZ extends BEHIND the
       origin (positive Z in local space = behind/far when the plane
       tilts toward camera) so the back wall of waves shrinks
       naturally with perspective. */
    var EX = 6.5;
    var EZ = 9.0;

    /* Dominant swell — biased toward rolling along the Z axis so the
       wavefronts come TOWARD the camera instead of arriving sideways.
       We jitter the direction within ±35° of pure +Z so each refresh
       looks fresh without losing the "rolling in" read. Lower freq
       (0.9–1.4) keeps wavelengths long: a handful of big swells
       crossing the plane, not tight ripples. */
    var swellBase = (Math.random() < 0.5 ? 1 : -1) * Math.PI * 0.5; /* roughly ±90° = aligned with Z */
    var swellAngle = swellBase + (Math.random() - 0.5) * (Math.PI * 0.40);
    var swellDirX  = Math.cos(swellAngle);
    var swellDirZ  = Math.sin(swellAngle);
    var swellFreq  = 0.9 + Math.random() * 0.5;
    var swellAmp   = 1.0;
    var swellPh    = Math.random() * Math.PI * 2;

    /* 2 cross-waves at random angles for secondary ripples. */
    var crossN = 2;
    var cross  = [];
    var maxCombinedAmp = swellAmp;
    for (var ci = 0; ci < crossN; ci++) {
      var a = Math.random() * Math.PI * 2;
      var amp = 0.20 + Math.random() * 0.15;
      cross.push({
        dx:   Math.cos(a),
        dz:   Math.sin(a),
        freq: 2.4 + Math.random() * 1.8,
        amp:  amp,
        ph:   Math.random() * Math.PI * 2
      });
      maxCombinedAmp += amp;
    }

    /* Y-displacement scale — much taller than the previous top-down
       version because the camera now sees vertical relief. Crests rise
       prominently above the trough surface, giving the cloud strong
       verticality and the felt 3D depth the user asked for. */
    var Y_SCALE = 0.65 / maxCombinedAmp;

    /* Rejection-sampled fill: try a candidate position, compute wave
       height there, and keep the point with probability biased toward
       crests. This is what makes the wave structure VISIBLE from a
       near-overhead angle — bright dense bands form along the crest
       lines, dim sparse regions form along troughs, exactly like
       sun-glinted ocean waves photographed from above. */
    var filled = 0;
    var maxAttempts = N * 6;
    var attempts = 0;
    while (filled < N && attempts < maxAttempts) {
      attempts++;
      var x = (Math.random() * 2 - 1) * EX;
      var z = (Math.random() * 2 - 1) * EZ;

      /* Project onto swell direction → 1D sine carries the wavefronts. */
      var swellU = x * swellDirX + z * swellDirZ;
      var waveRaw = Math.sin(swellU * swellFreq + swellPh) * swellAmp;
      for (var k = 0; k < cross.length; k++) {
        var u = x * cross[k].dx + z * cross[k].dz;
        waveRaw += Math.sin(u * cross[k].freq + cross[k].ph) * cross[k].amp;
      }

      /* Normalize wave value to [0, 1] then bias toward crests. Higher
         exponent → tighter crest bands, sharper edges, more rigid
         wave structure. */
      var norm = 0.5 + 0.5 * (waveRaw / maxCombinedAmp);
      var keepProb = Math.pow(norm, 4.0);
      /* Tiny background floor so troughs aren't pure black voids
         but stay much darker than the crests. */
      keepProb = 0.02 + 0.98 * keepProb;
      if (Math.random() > keepProb) continue;

      /* Minimal per-point jitter — keeps the wave shape geometrically
         clear instead of fuzzy. The breathing animation in tick()
         provides the visible motion; we don't need spatial noise on
         top of it. */
      var jx = (Math.random() - 0.5) * 0.015;
      var jy = (Math.random() - 0.5) * 0.008;
      var jz = (Math.random() - 0.5) * 0.015;

      positions[filled * 3]     = x + jx;
      positions[filled * 3 + 1] = waveRaw * Y_SCALE + jy;
      positions[filled * 3 + 2] = z + jz;
      filled++;
    }
    /* If we ran out of attempts (very high crest bias), trim the buffer. */
    if (filled < N) {
      positions = positions.subarray(0, filled * 3);
    }
    return { positions: positions };
  }

  /* ── Waves (legacy 3D-volume version) ──────────────────────────────────
     A wavy surface jittered in 3D — kept for reference/alternative use. */
  function procWaves() {
    var N = 50000;
    var positions = new Float32Array(N * 3);
    var SCALE = 2.0;
    /* Random wave parameters per load */
    var f1   = 0.6 + Math.random() * 0.9;
    var f2   = 1.1 + Math.random() * 0.8;
    var f3   = 0.4 + Math.random() * 0.6;
    var p1   = Math.random() * Math.PI * 2;
    var p2   = Math.random() * Math.PI * 2;
    var p3   = Math.random() * Math.PI * 2;
    var amp1 = 0.30 + Math.random() * 0.20;
    var amp2 = 0.15 + Math.random() * 0.15;
    var amp3 = 0.08 + Math.random() * 0.10;
    /* Random rotation of the whole wave field in XZ plane so the
       crests don't always run the same direction. */
    var rot  = Math.random() * Math.PI;
    var crot = Math.cos(rot), srot = Math.sin(rot);
    for (var i = 0; i < N; i++) {
      var x  = (Math.random() * 2 - 1) * SCALE;
      var z  = (Math.random() * 2 - 1) * SCALE;
      /* Rotate (x, z) so wave directionality varies */
      var rx = x * crot - z * srot;
      var rz = x * srot + z * crot;
      /* Multi-frequency height field */
      var y  = Math.sin(rx * f1 + p1) * amp1
             + Math.sin(rz * f1 + p2) * amp1
             + Math.sin((rx + rz) * f2 + p2) * amp2
             + Math.sin(rx * f3 - rz * f3 + p3) * amp3;
      /* Jitter for volumetric thickness */
      var jx = (Math.random() - 0.5) * 0.025;
      var jy = (Math.random() - 0.5) * 0.045;
      var jz = (Math.random() - 0.5) * 0.025;
      positions[i * 3]     = x + jx;
      positions[i * 3 + 1] = y + jy;
      positions[i * 3 + 2] = z + jz;
    }
    return normalize(positions);
  }

  /* ── Clouds ────────────────────────────────────────────────────────────
     5-8 gaussian-distributed point clusters at random positions, varying
     sizes. Each refresh moves the clusters around so the cloud silhouette
     is genuinely different. Reads as soft volumetric clumps drifting in
     space — closer to atmospheric phenomena than fixed geometry. */
  function procClouds() {
    var N = 45000;
    var positions = new Float32Array(N * 3);
    /* 4–6 cluster centres in a TIGHTER central region — keeps the mass
       readable as a unified cloud silhouette instead of spreading thin
       across the whole viewport. After normalize() rescales to unit
       radius, the cloud lands as a centered ~70% body. */
    var CLUSTERS = 4 + Math.floor(Math.random() * 3);
    var ctrs = [];
    for (var c = 0; c < CLUSTERS; c++) {
      ctrs.push({
        x:    (Math.random() - 0.5) * 1.4,
        y:    (Math.random() - 0.5) * 0.9,
        z:    (Math.random() - 0.5) * 1.4,
        size: 0.42 + Math.random() * 0.55,
        weight: 0.5 + Math.random()       /* relative point density */
      });
    }
    /* Cumulative weights for weighted random selection */
    var totalWeight = 0;
    for (var w = 0; w < ctrs.length; w++) totalWeight += ctrs[w].weight;
    for (var i = 0; i < N; i++) {
      /* Weighted random cluster pick */
      var pick = Math.random() * totalWeight;
      var ci = 0, accum = 0;
      for (var k = 0; k < ctrs.length; k++) {
        accum += ctrs[k].weight;
        if (pick <= accum) { ci = k; break; }
      }
      var ctr = ctrs[ci];
      /* Gaussian-ish radius via Box-Muller, then spherical coords */
      var u     = Math.max(1e-6, Math.random());
      var r     = Math.sqrt(-2 * Math.log(u)) * ctr.size * 0.50;
      var theta = Math.random() * Math.PI * 2;
      var phi   = Math.acos(2 * Math.random() - 1);
      positions[i * 3]     = ctr.x + r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = ctr.y + r * Math.cos(phi);
      positions[i * 3 + 2] = ctr.z + r * Math.sin(phi) * Math.sin(theta);
    }
    return normalize(positions);
  }

  /* ── Aizawa strange attractor ─────────────────────────────────────────
     A chaotic dynamical system whose trajectory traces a swirling 3D form
     that resembles coiled smoke or a fractal scribble. The math is pure
     differential equations — no scan needed. Reads as "system output"
     rather than "graphics demo", which fits the BIOS terminal aesthetic.

     Parameters tuned for the canonical Aizawa form. Warm-up phase discards
     the first 1500 steps (the trajectory transient before it settles onto
     the strange attractor). Then we sample 60K points along the curve,
     adding tiny per-point jitter so the trajectory reads as a fuzzy
     volumetric cloud instead of a thin connected line. */
  function procAizawa() {
    var N = 60000;
    var positions = new Float32Array(N * 3);
    var a = 0.95, b = 0.7, c = 0.6, d = 3.5, e = 0.25, f = 0.1;
    var x = 0.1, y = 0, z = 0;
    var dt = 0.01;

    /* Warm-up — let the system settle onto the attractor before recording. */
    for (var w = 0; w < 1500; w++) {
      var dx = (z - b) * x - d * y;
      var dy = d * x + (z - b) * y;
      var dz = c + a * z - (z * z * z) / 3 - (x * x + y * y) * (1 + e * z) + f * z * (x * x * x);
      x += dx * dt; y += dy * dt; z += dz * dt;
    }

    for (var i = 0; i < N; i++) {
      var dxi = (z - b) * x - d * y;
      var dyi = d * x + (z - b) * y;
      var dzi = c + a * z - (z * z * z) / 3 - (x * x + y * y) * (1 + e * z) + f * z * (x * x * x);
      x += dxi * dt; y += dyi * dt; z += dzi * dt;
      /* Per-point random jitter blurs the trajectory line into a fuzzy
         cloud (volumetric feel) instead of a single hairline curve. */
      positions[i * 3]     = x + (Math.random() - 0.5) * 0.025;
      positions[i * 3 + 1] = y + (Math.random() - 0.5) * 0.025;
      positions[i * 3 + 2] = z + (Math.random() - 0.5) * 0.025;
    }
    return normalize(positions);
  }

  /* ── Cloth drape simulation ───────────────────────────────────────────
     Verlet-integrated grid of points pinned at the top center, falling
     under gravity with neighbor-distance constraints. After ~600 settling
     steps the cloth hangs as a draped form — reads as fabric on an
     invisible mannequin. Less generic than a sphere, more on-brand for
     a fashion shop than abstract math. */
  function procClothDrape() {
    var COLS = 80, ROWS = 110;
    var REST = 0.035;
    var GRAVITY = 0.00065;
    var STEPS = 800;
    var DAMPING = 0.97;

    var N = COLS * ROWS;
    var pos = new Float32Array(N * 3);
    var old = new Float32Array(N * 3);
    var fixed = new Uint8Array(N);

    /* Init: flat horizontal sheet centered on origin. */
    for (var r = 0; r < ROWS; r++) {
      for (var c = 0; c < COLS; c++) {
        var i = r * COLS + c;
        pos[i * 3]     = (c - COLS / 2) * REST;
        pos[i * 3 + 1] = (ROWS / 2 - r) * REST + 1.2;
        pos[i * 3 + 2] = 0;
        old[i * 3]     = pos[i * 3];
        old[i * 3 + 1] = pos[i * 3 + 1];
        old[i * 3 + 2] = pos[i * 3 + 2];
      }
    }
    /* Pin the top center 8 points so cloth hangs from a "shoulder". */
    var mid = Math.floor(COLS / 2);
    for (var px = mid - 4; px <= mid + 4; px++) fixed[px] = 1;

    function relax(a, b) {
      var ax = pos[a * 3], ay = pos[a * 3 + 1], az = pos[a * 3 + 2];
      var bx = pos[b * 3], by = pos[b * 3 + 1], bz = pos[b * 3 + 2];
      var dx = bx - ax, dy = by - ay, dz = bz - az;
      var len = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (len === 0) return;
      var diff = ((len - REST) / len) * 0.5;
      var ox = dx * diff, oy = dy * diff, oz = dz * diff;
      var af = fixed[a], bf = fixed[b];
      if (!af && !bf) {
        pos[a * 3] += ox; pos[a * 3 + 1] += oy; pos[a * 3 + 2] += oz;
        pos[b * 3] -= ox; pos[b * 3 + 1] -= oy; pos[b * 3 + 2] -= oz;
      } else if (!af) {
        pos[a * 3] += ox * 2; pos[a * 3 + 1] += oy * 2; pos[a * 3 + 2] += oz * 2;
      } else if (!bf) {
        pos[b * 3] -= ox * 2; pos[b * 3 + 1] -= oy * 2; pos[b * 3 + 2] -= oz * 2;
      }
    }

    for (var s = 0; s < STEPS; s++) {
      /* Verlet integration: pos += (pos - old)*damping + gravity */
      for (var v = 0; v < N; v++) {
        if (fixed[v]) continue;
        var vx = (pos[v * 3]     - old[v * 3])     * DAMPING;
        var vy = (pos[v * 3 + 1] - old[v * 3 + 1]) * DAMPING - GRAVITY;
        var vz = (pos[v * 3 + 2] - old[v * 3 + 2]) * DAMPING;
        old[v * 3]     = pos[v * 3];
        old[v * 3 + 1] = pos[v * 3 + 1];
        old[v * 3 + 2] = pos[v * 3 + 2];
        pos[v * 3]     += vx;
        pos[v * 3 + 1] += vy;
        pos[v * 3 + 2] += vz;
      }
      /* Constraint pass — relax each spring twice for stability. */
      for (var pass = 0; pass < 2; pass++) {
        for (var rr = 0; rr < ROWS; rr++) {
          for (var cc = 0; cc < COLS; cc++) {
            var ii = rr * COLS + cc;
            if (cc < COLS - 1) relax(ii, ii + 1);
            if (rr < ROWS - 1) relax(ii, ii + COLS);
          }
        }
      }
    }

    /* Slight per-point z-jitter for visible fabric "thickness". */
    for (var p = 0; p < N; p++) pos[p * 3 + 2] += (Math.random() - 0.5) * 0.012;
    return normalize(pos);
  }

  /* ── Perlin-noise displaced sphere ────────────────────────────────────
     Cheaper than cloth, more interesting than a plain sphere. Each
     point's radius is offset by 3D noise derived from its angular
     position, producing an asteroid/lumpy form. Uses a fast custom
     noise (value-noise with hash) — no external lib needed. */
  function procPerlinSphere() {
    var N = 35000;
    var positions = new Float32Array(N * 3);
    function hash(x, y, z) {
      var n = Math.sin(x * 12.9898 + y * 78.233 + z * 37.719) * 43758.5453;
      return n - Math.floor(n);
    }
    function noise(x, y, z) {
      var xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
      var xf = x - xi, yf = y - yi, zf = z - zi;
      var u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf), w = zf * zf * (3 - 2 * zf);
      var c000 = hash(xi, yi, zi),     c100 = hash(xi + 1, yi, zi);
      var c010 = hash(xi, yi + 1, zi), c110 = hash(xi + 1, yi + 1, zi);
      var c001 = hash(xi, yi, zi + 1), c101 = hash(xi + 1, yi, zi + 1);
      var c011 = hash(xi, yi + 1, zi + 1), c111 = hash(xi + 1, yi + 1, zi + 1);
      var x00 = c000 + u * (c100 - c000), x10 = c010 + u * (c110 - c010);
      var x01 = c001 + u * (c101 - c001), x11 = c011 + u * (c111 - c011);
      var y0 = x00 + v * (x10 - x00),     y1 = x01 + v * (x11 - x01);
      return y0 + w * (y1 - y0);
    }
    for (var i = 0; i < N; i++) {
      var uu, vv, ss;
      do {
        uu = Math.random() * 2 - 1; vv = Math.random() * 2 - 1; ss = uu * uu + vv * vv;
      } while (ss >= 1);
      var ff = Math.sqrt(1 - ss);
      var sx = 2 * uu * ff, sy = 2 * vv * ff, sz = 1 - 2 * ss;
      /* Displace radially by noise at multiple frequencies */
      var n = noise(sx * 2.0, sy * 2.0, sz * 2.0) * 0.6
            + noise(sx * 5.0, sy * 5.0, sz * 5.0) * 0.2;
      var r = 1.4 + n * 0.4;
      positions[i * 3]     = sx * r;
      positions[i * 3 + 1] = sy * r;
      positions[i * 3 + 2] = sz * r;
    }
    return normalize(positions);
  }

  /* Baseline sphere — kept around for comparison. The original placeholder. */
  function procSphere() {
    var N = 30000;
    var positions = new Float32Array(N * 3);
    for (var i = 0; i < N; i++) {
      var u, v, s;
      do { u = Math.random() * 2 - 1; v = Math.random() * 2 - 1; s = u * u + v * v; } while (s >= 1);
      var f = Math.sqrt(1 - s);
      var r = 1.5 + (Math.random() - 0.5) * 0.18;
      positions[i * 3]     = 2 * u * f * r;
      positions[i * 3 + 1] = 2 * v * f * r;
      positions[i * 3 + 2] = (1 - 2 * s) * r;
    }
    return { positions: positions };
  }
})();

/* ══════════════════════════════════════════════════════════════════════
   Portal FX — CSS-level mouse effects on the rendered pointcloud canvas.
   No interaction with the Three.js render itself; pure CSS transforms +
   filters applied to the <canvas> element so the existing renderer keeps
   working unchanged while the canvas image gets a layer of post-process
   responsiveness.

   (A) Perspective tilt — the canvas rotates a few degrees toward where
       the cursor is. Layered on top of the existing in-scene parallax
       so the form reads as having both a moving camera AND a tilted
       monitor surface.
   (C) Velocity chromatic aberration — when the mouse moves fast,
       stacked red/cyan drop-shadow filters split the canvas channels
       briefly, decaying back to neutral within ~200ms after the mouse
       settles. Reads as signal-tearing on rapid input.

   Both effects stop on bios:portal-done so the catalog is unaffected. */
(function biosPortalFx() {
  'use strict';
  var canvas = document.getElementById('bios-portal-pointcloud');
  if (!canvas) return;
  /* Skip on touch — mouse motion is fundamentally different on touch
     devices (no hover/move stream), and the FX would just look static. */
  if ('ontouchstart' in window) return;

  /* Smoothed mouse position (-1..+1 of viewport). lastX/Y track raw
     position frame-to-frame so we can compute velocity. */
  var mx = 0, my = 0, tx = 0, ty = 0;
  var lastX = window.innerWidth / 2, lastY = window.innerHeight / 2;
  var primed = false;
  /* Aberration amplitude in pixels; accumulates on mouse motion, decays
     each frame so it returns to 0 a few hundred ms after the mouse
     stops moving. */
  var chromAmp = 0;

  document.addEventListener('mousemove', function (e) {
    tx = (e.clientX / window.innerWidth)  * 2 - 1;
    ty = (e.clientY / window.innerHeight) * 2 - 1;
    if (primed) {
      var dx = e.clientX - lastX;
      var dy = e.clientY - lastY;
      var speed = Math.sqrt(dx * dx + dy * dy);
      /* Speed scaled, capped — sane max of 6px aberration even on flicks. */
      chromAmp = Math.min(chromAmp + speed * 0.045, 6.0);
    }
    lastX = e.clientX;
    lastY = e.clientY;
    primed = true;
  }, { passive: true });

  var stopped = false;
  document.addEventListener('bios:portal-done', function () {
    stopped = true;
    /* Clear inline styles so the canvas doesn't carry residual transform
       into the catalog view (where the portal canvas is hidden anyway,
       but we leave a clean DOM). */
    canvas.style.transform = '';
    canvas.style.filter = '';
  });

  function tick() {
    if (stopped) return;
    requestAnimationFrame(tick);

    /* Lerp toward the target position — gentler than the in-scene
       parallax (0.08 vs 0.06) so the CSS layer feels distinct. */
    mx += (tx - mx) * 0.07;
    my += (ty - my) * 0.07;

    /* Aberration decays exponentially — drops to ~0.03 of peak in
       about 240ms at 60fps. Cleared to literal zero once below a
       perceptual threshold to skip the filter set entirely (perf). */
    chromAmp *= 0.86;
    if (chromAmp < 0.08) chromAmp = 0;

    /* Tilt: rotate around X by mouse-Y, around Y by mouse-X. Numbers
       are deliberately modest (max ~3.5°) so it reads as a subtle
       lean rather than a "tilting playing card" effect. scale(1.06)
       keeps the canvas edges off-screen as it tilts so we never
       see black bars peek in at the corners. */
    var tiltX = (my * -2.5).toFixed(2);
    var tiltY = (mx *  3.5).toFixed(2);
    canvas.style.transformOrigin = '50% 50%';
    canvas.style.transform =
      'scale(1.06) perspective(1400px) ' +
      'rotateX(' + tiltX + 'deg) rotateY(' + tiltY + 'deg)';

    /* Chromatic aberration via stacked drop-shadow filters — when the
       mouse settles and chromAmp is 0, clear the filter entirely so the
       canvas falls back to pure rendered output (no perf cost). */
    if (chromAmp > 0) {
      var amp = chromAmp.toFixed(2);
      canvas.style.filter =
        'drop-shadow(-' + amp + 'px 0 0 rgba(217, 87, 87, 0.42)) ' +
        'drop-shadow('  + amp + 'px 0 0 rgba(87, 217, 217, 0.42))';
    } else if (canvas.style.filter !== '') {
      canvas.style.filter = '';
    }
  }
  tick();
})();
